import React, { useState, useEffect, useCallback, useRef } from "react";
import { Plus, Loader2, AlertCircle, RefreshCw, FileText, Image as ImageIcon } from "lucide-react";
import { accountService } from "../../../api/accountService";

function formatDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }) + " WIB";
}

const KIND_LABEL = {
  INCOME: "Pendapatan",
  NON_INCOME: "Non-Pendapatan",
  EXPENSE: "Operasional / Biaya",
  PURCHASE: "Pembelian / Kulakan",
  MANUAL: "Manual",
  SALES: "Penjualan",
  SALES_REFUND: "Pembatalan Transaksi",
  SALES_RETURN: "Retur Penjualan",
  RETURN: "Retur Penjualan",
  BIAYA: "Operasional / Biaya",
};

export default function ShiftMutationTable({
  activeShift,
  onAddMutation,
  onLoadCount,
  refreshKey,
}) {
  const [mutations, setMutations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const onLoadCountRef = useRef(onLoadCount);
  useEffect(() => {
    onLoadCountRef.current = onLoadCount;
  }, [onLoadCount]);

  const fetchGlobalMutations = useCallback(async () => {
    if (!activeShift?.opened_at) {
      setMutations([]);
      onLoadCountRef.current?.(0);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch all active accounts
      const accountsRes = await accountService.list({ page: 1, limit: 100 });
      const accounts = accountsRes?.data || accountsRes || [];
      const activeAccounts = accounts.filter((a) => a.status === "ACTIVE" || a.status === "Aktif");

      const shiftStart = new Date(activeShift.opened_at).getTime();

      // Fetch mutations for each active account
      const mutationPromises = activeAccounts.map(async (acc) => {
        try {
          const res = await accountService.getMutations(acc.id, { page: 1, limit: 100 });
          const items = res?.data || res || [];
          return (Array.isArray(items) ? items : []).map((m) => ({
            ...m,
            account_id: acc.id,
            account_name: acc.name,
            account_type: acc.type,
          }));
        } catch {
          return [];
        }
      });

      const results = await Promise.all(mutationPromises);
      const allMutations = results.flat();

      // Filter by current active shift start time
      const shiftMutations = allMutations.filter((m) => {
        const mTime = new Date(m.created_at || m.date || m.trx_date).getTime();
        return mTime >= shiftStart;
      });

      // Sort descending (newest first)
      shiftMutations.sort(
        (a, b) => new Date(b.created_at || b.date).getTime() - new Date(a.created_at || a.date).getTime()
      );

      setMutations(shiftMutations);
      onLoadCountRef.current?.(shiftMutations.length);
    } catch (err) {
      console.error("Error fetching shift mutations:", err);
      setError(err?.message || "Gagal memuat daftar mutasi shift");
    } finally {
      setLoading(false);
    }
  }, [activeShift?.opened_at]);

  useEffect(() => {
    fetchGlobalMutations();
  }, [fetchGlobalMutations, refreshKey]);

  return (
    <div className="space-y-4">
      {/* Header action bar in Mutasi Tab */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-slate-50 rounded-xl border border-slate-200/80">
        <div>
          <h3 className="text-sm font-bold text-slate-800">
            Daftar Mutasi Kasir & Keuangan Shift
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Catatan transaksi uang masuk / keluar semua akun selama shift ini berlangsung.
          </p>
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <button
            onClick={fetchGlobalMutations}
            disabled={loading}
            className="p-2 text-slate-500 hover:text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors shadow-xs"
            title="Refresh Data">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-teal-600" : ""}`} />
          </button>
          <button
            onClick={onAddMutation}
            className="px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            Tambah Mutasi
          </button>
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
        <table className="w-full text-left text-xs">
          <thead className="bg-slate-50 border-b border-slate-200 font-semibold text-slate-600 uppercase tracking-wider text-[11px]">
            <tr>
              <th className="px-4 py-3">Akun Keuangan</th>
              <th className="px-4 py-3">Kategori</th>
              <th className="px-4 py-3">Waktu</th>
              <th className="px-4 py-3">Jenis / Ref</th>
              <th className="px-4 py-3 text-right">Nominal</th>
              <th className="px-4 py-3">Keterangan</th>
              <th className="px-4 py-3 text-center">Lampiran</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 font-medium text-slate-700">
            {loading ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600 mb-2" />
                  Memuat data mutasi shift...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-rose-500">
                  <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                  {error}
                </td>
              </tr>
            ) : mutations.length === 0 ? (
              <tr>
                <td colSpan="7" className="py-12 text-center text-slate-400">
                  <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                  Belum ada catatan mutasi pada shift ini.
                </td>
              </tr>
            ) : (
              mutations.map((m) => {
                const isIncome = m.category === "IN" || m.category === "MASUK";
                const amount = Number(m.amount || 0);
                const proofUrl = m.proof_url || m.attachments?.[0]?.url;

                return (
                  <tr key={m.id || Math.random()} className="hover:bg-slate-50/70 transition-colors">
                    <td className="px-4 py-3.5 font-bold text-slate-900">
                      {m.account_name || "Akun Keuangan"}
                    </td>
                    <td className="px-4 py-3.5">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                          isIncome
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-orange-50 text-orange-700 border-orange-200"
                        }`}>
                        {isIncome ? "Uang Masuk" : "Uang Keluar"}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-slate-500 font-mono text-[11px]">
                      {formatDateTime(m.created_at || m.date)}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600">
                      {KIND_LABEL[m.kind] || m.kind || m.reference_type || "-"}
                    </td>
                    <td
                      className={`px-4 py-3.5 text-right font-bold text-xs ${
                        isIncome ? "text-emerald-600" : "text-orange-600"
                      }`}>
                      {isIncome ? "+" : "-"}Rp {amount.toLocaleString("id-ID")}
                    </td>
                    <td className="px-4 py-3.5 text-slate-600 max-w-xs truncate" title={m.notes || m.description || "-"}>
                      {m.notes || m.description || "-"}
                    </td>
                    <td className="px-4 py-3.5 text-center">
                      {proofUrl ? (
                        <a
                          href={proofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-teal-600 hover:text-teal-700 font-semibold hover:underline"
                          title="Lihat Bukti">
                          <ImageIcon className="w-3.5 h-3.5" />
                          Bukti
                        </a>
                      ) : (
                        <span className="text-slate-300">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
